// src/views/admin/SlaAiCommandCenter.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — SLA AI Command Center  (route: /admin/sla-ai)
//
//   Overview     live SLA KPIs, engine + mode, "Run sweep now", jobs breaching / at
//                risk with AI preview, latest AI decisions
//   Decisions    the full decision log (filters, explainer, approve / reject / revert)
//   Scoreboard   tech & contractor SLA reliability from graded work
//   Inbox        SLA AI notifications
//   How it works thresholds and the model, in plain words
// Deep link: /admin/sla-ai?decision=<id> opens that decision (used by the escalation emails).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, Heading, HStack, Icon, List, ListIcon, ListItem, SimpleGrid,
    Tab, TabList, TabPanel, TabPanels, Tabs, Text, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdChevronRight } from 'react-icons/md';
import { useSlaAiActions, useSlaAiOverview } from '../../hooks/useSlaAi';
import SlaAiOverviewStrip from '../../components/sla/ai/SlaAiOverviewStrip';
import SlaHotJobsTable from '../../components/sla/ai/SlaHotJobsTable';
import SlaAiDecisionFeed from '../../components/sla/ai/SlaAiDecisionFeed';
import SlaScoreboard from '../../components/sla/ai/SlaScoreboard';
import SlaAiInbox from '../../components/sla/ai/SlaAiInbox';
import SlaDecisionExplainer from '../../components/sla/ai/SlaDecisionExplainer';

const TABS = ['overview', 'decisions', 'scoreboard', 'inbox', 'how'];

function HowItWorks({ thresholds = {}, mode }) {
    const muted = useColorModeValue('gray.600', 'gray.400');
    const card = useColorModeValue('white', 'gray.800');
    const items = [
        ['Grading', 'Every job with an SLA is graded 0–100 (A–F): finished on time 45%, first response 15%, work quality 25% (QA audit / checklist), evidence & rework 15%. A missed deadline caps the grade at 79 (slightly late) or 59 (badly late). Hold time pauses the clock.'],
        ['At risk', `When ${thresholds.warn_pct ?? 80}% of the SLA window is used, the technician and supervisors get one warning.`],
        ['Deadline passed', 'The job is escalated one level, supervisors and managers are emailed and notified in-app, and the assignee gets a breach on their record.'],
        ['Reassignment', `The model ranks other technicians and contractors on SLA reliability 35%, free capacity 20%, distance 20%, similar jobs done 15% and team 10%. ${mode === 'recommend' ? 'In recommend mode a supervisor approves the move.' : `If the top pick scores ≥ ${thresholds.min_candidate_score ?? 50} with ≥ ${Math.round((thresholds.min_confidence ?? 0.55) * 100)}% confidence, the job moves automatically.`}`],
        ['When it holds back', `It does not pull a job from a technician who started on site less than ${thresholds.grace_min ?? 60} min ago, or after ${thresholds.max_auto_reassign ?? 2} moves — it escalates and says why instead.`],
        ['Re-escalation', `Still not finished ${thresholds.reescalate_after_min ?? 120} min later → next level (max ${thresholds.max_level ?? 3}).`],
        ['Fairness', 'A technician handed a job after the deadline is graded against a fresh handover window, not the missed customer deadline.'],
        ['Oversight', 'Every action is logged with its reasons and the ranked candidates. Supervisors can approve, reject or revert, and each review trains the dispatch model.'],
    ];
    return (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {items.map(([t, d]) => (
                <Box key={t} bg={card} borderWidth="1px" borderRadius="xl" p={4}>
                    <Text fontWeight="800" mb={1}>{t}</Text>
                    <Text fontSize="sm" color={muted}>{d}</Text>
                </Box>
            ))}
        </SimpleGrid>
    );
}

export default function SlaAiCommandCenter() {
    const initialTab = useMemo(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const t = TABS.indexOf(p.get('tab'));
            return t >= 0 ? t : 0;
        } catch { return 0; }
    }, []);
    const deepDecision = useMemo(() => { try { return new URLSearchParams(window.location.search).get('decision'); } catch { return null; } }, []);
    const [tab, setTab] = useState(initialTab);
    const [decisionFilter, setDecisionFilter] = useState('all');
    const [openDecision, setOpenDecision] = useState(deepDecision ? { id: deepDecision } : null);
    const explainer = useDisclosure({ defaultIsOpen: !!deepDecision });
    const overview = useSlaAiOverview();
    const actions = useSlaAiActions();
    const toast = useToast();

    const bg = useColorModeValue('gray.50', 'gray.900');
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const gradient = useColorModeValue('linear(to-r, purple.600, teal.500)', 'linear(to-r, purple.300, teal.300)');
    const { refresh } = overview;

    useEffect(() => {
        try {
            const u = new URL(window.location.href);
            u.searchParams.set('tab', TABS[tab]);
            window.history.replaceState(window.history.state, '', u.toString());
        } catch { /* ignore */ }
    }, [tab]);

    const runNow = useCallback(async () => {
        try {
            const r = await actions.runNow();
            toast({
                title: r.skipped ? 'Sweep skipped' : 'Sweep finished',
                description: r.skipped || `${r.scanned} jobs checked · ${r.warned} warned · ${r.escalated} escalated · ${r.reassigned} reassigned · ${r.recommended} recommended · ${r.graded} graded`,
                status: r.errors ? 'warning' : 'success', duration: 7000, isClosable: true,
            });
            refresh();
        } catch (e) {
            toast({ title: 'Could not run the sweep', description: e.message, status: 'error', duration: 6000, isClosable: true });
        }
    }, [actions, toast, refresh]);

    const showPending = useCallback(() => { setDecisionFilter('pending_approval'); setTab(1); }, []);
    const openFromInbox = useCallback((d) => { setOpenDecision(d); explainer.onOpen(); }, [explainer]);
    const forbidden = /permission|403/i.test(overview.error || '');

    return (
        <Box bg={bg} minH="100vh" px={{ base: 4, md: 6 }} pt="10px" pb={10}>
            <Flex justify="space-between" align="center" mb={5} gap={3} flexWrap="wrap">
                <HStack spacing={3}>
                    <Flex bgGradient={gradient} p={3} borderRadius="xl" color="white"><Icon as={MdAutoAwesome} boxSize={7} /></Flex>
                    <Box>
                        <Heading size="lg" bgGradient={gradient} bgClip="text">SLA AI Command Center</Heading>
                        <Text fontSize="sm" color={muted}>FlowSense AI grades every job against its SLA, escalates breaches and reassigns work — and explains each decision.</Text>
                    </Box>
                </HStack>
                <SlaAiInbox variant="bell" onOpenDecision={openFromInbox} />
            </Flex>

            {overview.error && (
                <Alert status={forbidden ? 'warning' : 'error'} borderRadius="xl" mb={4}>
                    <AlertIcon />
                    <AlertDescription flex="1">{forbidden ? 'The SLA AI overview is for supervisors, managers and admins.' : overview.error}</AlertDescription>
                    {!forbidden && <Button size="sm" onClick={() => overview.reload()}>Retry</Button>}
                </Alert>
            )}

            <Tabs index={tab} onChange={setTab} variant="soft-rounded" colorScheme="purple" isLazy>
                <TabList bg={panel} p={2} borderRadius="2xl" borderWidth="1px" borderColor={border} mb={4} overflowX="auto">
                    <Tab>Overview</Tab>
                    <Tab>Decisions{overview.data?.pending_approval ? <Badge ml={2} colorScheme="blue">{overview.data.pending_approval}</Badge> : null}</Tab>
                    <Tab>Scoreboard</Tab>
                    <Tab>Inbox</Tab>
                    <Tab>How it works</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel p={0}>
                        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }} mb={4}>
                            <SlaAiOverviewStrip overview={overview.data} loading={overview.loading} onRunNow={runNow} running={actions.busy === 'run'} onShowPending={showPending} />
                        </Box>
                        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
                            <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                                <SlaHotJobsTable jobs={overview.data?.hot || []} loading={overview.loading} onChanged={refresh} />
                            </Box>
                            <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                                <SlaAiDecisionFeed compact title="Latest AI decisions" pageSize={8} />
                            </Box>
                        </SimpleGrid>
                    </TabPanel>
                    <TabPanel p={0}>
                        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                            <SlaAiDecisionFeed key={decisionFilter} defaultStatus={decisionFilter} pageSize={25} />
                        </Box>
                    </TabPanel>
                    <TabPanel p={0}>
                        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                            <SlaScoreboard />
                        </Box>
                    </TabPanel>
                    <TabPanel p={0}>
                        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
                            <SlaAiInbox onOpenDecision={openFromInbox} />
                        </Box>
                    </TabPanel>
                    <TabPanel p={0}>
                        <HowItWorks thresholds={overview.data?.thresholds} mode={overview.data?.mode} />
                        <List spacing={1} mt={4} fontSize="sm" color={muted}>
                            <ListItem><ListIcon as={MdChevronRight} />Model: {overview.data?.engine?.model_version || 'sla-grade-1.0'} — Python ML service, with an identical Node mirror when ML is unreachable.</ListItem>
                            <ListItem><ListIcon as={MdChevronRight} />Mode: {overview.data?.mode || '—'} (SLA_AI_MODE = auto | recommend | off).</ListItem>
                        </List>
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <SlaDecisionExplainer
                isOpen={explainer.isOpen}
                onClose={explainer.onClose}
                decisionId={openDecision?.id}
                decision={openDecision?.summary ? openDecision : null}
                onChanged={refresh}
            />
        </Box>
    );
}
